import { borrowerCreditsDataModule } from '@perpay-web/fintech/dataModules/composed/borrowerCredits';
import { cardActivityDataModule } from '@perpay-web/fintech/dataModules/composed/cardActivity';
import { cardCreateAutoPayDataModule } from '@perpay-web/fintech/dataModules/composed/cardCreateAutoPay';
import { cardDetailsNotificationsDataModule } from '@perpay-web/fintech/dataModules/composed/cardDetailsNotifications';
import { cardManualPaymentSetupDataModule } from '@perpay-web/fintech/dataModules/composed/cardManualPaymentSetup';
import { cardOnboardingSwitcherDataModule } from '@perpay-web/fintech/dataModules/composed/cardOnboardingSwitcher';
import { cardPaymentSummaryDataModule } from '@perpay-web/fintech/dataModules/composed/cardPaymentSummary';
import { cardStatementsDataModule } from '@perpay-web/fintech/dataModules/composed/cardStatements';
import { checkoutDataModule } from '@perpay-web/fintech/dataModules/composed/checkout';
import { dashboardDataModule as dashboardV2DataModule } from '@perpay-web/fintech/dataModules/composed/dashboardV2';
import { directDepositDetailsDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositDetails';
import { directDepositUpdateEmployerAndAmountDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositUpdateEmployerAndAmount';
import { directDepositUpdateManualPaymentSetupDataModule } from '@perpay-web/fintech/dataModules/composed/directDepositUpdateManualPaymentSetup';
import { fetchCardDetailsV2DataModule } from '@perpay-web/fintech/dataModules/composed/fetchCardDetailsV2';
import { marketplaceDetailsDataModule } from '@perpay-web/fintech/dataModules/composed/marketplaceDetails';
import { marketplaceDetailsNotificationsDataModule } from '@perpay-web/fintech/dataModules/composed/marketplaceDetailsNotifications';
import { ordersDataModule } from '@perpay-web/fintech/dataModules/composed/orders';
import { partnerOnboardingDataModule } from '@perpay-web/fintech/dataModules/composed/partnerOnboarding';
import { payrollInstructionsFlowDataModule } from '@perpay-web/fintech/dataModules/composed/payrollInstructionsFlow';
import { perpayPlusDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlus';
import { perpayPlusSoloDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlusSolo';
import { perpayPlusSoloApplicationDataModule } from '@perpay-web/fintech/dataModules/composed/perpayPlusSoloApplication';
import { pinwheelDataModule } from '@perpay-web/fintech/dataModules/composed/pinwheel';
import { updateDirectDepositDataModule } from '@perpay-web/fintech/dataModules/composed/updateDirectDeposit';
import { bankAccountsForCardAutoPayDataModule } from '@perpay-web/fintech/dataModules/crud/bankAccountsForCardAutoPay';
import { cardAccountApplicationCrudDataModule } from '@perpay-web/fintech/dataModules/crud/cardAccountApplication';
import { featureEnrollmentsForPerpayPlusCrudDataModule } from '@perpay-web/fintech/dataModules/crud/featureEnrollmentsForPerpayPlus';
import { userInfoForCardOnboardingMVP2CrudDataModule } from '@perpay-web/fintech/dataModules/crud/userInfoForCardOnboardingMVP2';
import { payrollSetupPaychexError } from '@perpay-web/fintech/epics/apis/paychexDirectDeposit';
import { fetchTrustPilotLink } from '@perpay-web/fintech/epics/apis/trustPilotLink';
import { payrollPaymentSetupSuccess } from '@perpay-web/fintech/epics/ui/payrollPaymentSetup';
import {
    analyticsIterablePushNotificationOpen,
    navigateOnNativeUrlPush,
} from '@perpay-web/fintech/epics/utils/push';
import {
    actionsToNativeFromWeb,
    actionsToWebFromNative,
} from '@perpay-web/native-client/epics';
import { EpicManager } from '@perpay-web/services/EpicManager';
import { hookActionEpic } from '@perpay-web/services/singletons';
import {
    cleanupAlgoliaOnLogout,
    cleanupMagentoTokenOnLogout,
} from '@perpay-web/storefront/epics/authentication/storefrontTokens';

import { reportABTestVariant as reportABTestVariantForDataModule } from './analytics/abTests';
import {
    getABTest,
    reportABTestVariant,
    fetchABTests,
    fetchABTestsV2,
} from './apis/abTests';
import { getAccountDeactivationEligibility } from './apis/accountDeactivationEligibility';
import { getAccountDeactivationReasons } from './apis/accountDeactivationReasons';
import { fetchAccountSummary } from './apis/accountSummary';
import { fetchAds } from './apis/ads';
import { analyticsBuffer, updateUtmCookie } from './apis/analytics';
import { appUpdate } from './apis/appUpdate';
import {
    authenticationSuccess,
    login,
    refreshToken,
    logoutToken,
    resetPassword,
    logoutAuthService,
    initializeIdentity,
    resendMFA,
    verifyMFA,
    passkeyAuthenticateCompleteSuccess,
    fetchAuthTokensSuccess,
    passkeyRegisterLogin,
    passkeyRegisterSignup,
    openBiometricRegistrationFailureModal,
    biometricGateEnabledInitialize,
} from './apis/authentication';
import { fetchInitiatedAutoPayPayments } from './apis/autoPayPayments';
import {
    createAutoPaySchedule,
    fetchAutoPaySchedules,
    updateAutoPaySchedule,
} from './apis/autoPaySchedules';
import {
    fetchBanks,
    createBank,
    verifyBank,
    createBankAccount,
    fetchBankAccounts,
} from './apis/banks';
import { fetchBorrowerCreditSummary } from './apis/borrowerCreditSummary';
import { fetchBorrowerCredits } from './apis/borrowerCredits';
import {
    createCardAccountApplications,
    fetchCardAccountApplication,
    pollCardAccountApplicationStatus,
    submitCardApplication,
    submitCardApplicationError,
    updateCardAccountApplication,
    pollCardApplicationStatus,
    pollCardApplicationStatusRedirect,
    pollCardApplicationStatusTimeoutRedirect,
} from './apis/cardAccountApplications';
import { fetchCardAccount } from './apis/cardAccounts';
import { cardActivate } from './apis/cardActivation';
import { fetchCardActivity } from './apis/cardActivity';
import { fetchCardDashboardSummary } from './apis/cardDashboardSummary';
import { uploadCardDocuments } from './apis/cardDocuments';
import { freezeCard, unfreezeCard } from './apis/cardFreeze';
import {
    validateAddressForCardMigration,
    handleCardMigrationSuccess,
} from './apis/cardMigration';
import {
    downloadCardStatement,
    fetchCardStatements,
} from './apis/cardStatements';
import { fetchCompanies, fetchCompaniesSelfOnboarding } from './apis/companies';
import { fetchCoreSummary } from './apis/coreSummary';
import { fetchCreditLimits, updateCreditLimit } from './apis/creditLimits';
import { fetchCreditScoreHistory } from './apis/creditScoreHistory';
import { fetchCreditScoresSummary } from './apis/creditScoresSummary';
import { fetchPlatformDashboardNotifications } from './apis/dashboardPlatformNotifications';
import {
    createDeduction,
    fetchDeductions,
    fetchDeductionsForDataModule,
    pinwheelTransitionToModal,
    pinwheelSplitTransitionToModal,
    updateDirectDepositPinwheelTransitionToModal,
    updateDeduction,
    updateDeductionAccountId,
    perpayPlusSoloPinwheelStepTransition,
} from './apis/deductions';
import { submitDefaultAddress } from './apis/defaultAddress';
import { createDeposit } from './apis/deposits';
import { fetchDirectDepositDashboardSummary } from './apis/directDepositSummary';
import { submitEmail } from './apis/email';
import { createEvent } from './apis/events';
import { fetchExternalCards, createExternalCard } from './apis/externalCards';
import {
    enrollFeature,
    reEnableFeature,
    fetchEnrolledFeatures,
    disableFeature,
    fetchFeatureEnrollments,
    createFeatureEnrollment,
} from './apis/featureEnrollments';
import { fetchFeatureSwitch } from './apis/featureSwitch';
import { fetchFeatures } from './apis/features';
import { fetchGeneralAccountSummary } from './apis/generalAccountSummary';
import { verifyIdentity } from './apis/identityVerification';
import {
    fetchPayrollInstructions,
    fetchPayrollInstructionsForDataModule,
} from './apis/instructions';
import {
    getJobs,
    createPrimaryJob,
    fetchJobsForDataModule,
    updateJob,
} from './apis/jobs';
import { fetchLearnMore } from './apis/learnMore';
import { fetchMarketplaceDashboardSummary } from './apis/marketplaceDashboardSummary';
import {
    marketplacePartnerOnboardingSignup,
    fetchMarketplaceSummaryForOnboarding,
    submitMarketplaceCardApplicationSuccess,
} from './apis/marketplacePartnerOnboarding';
import {
    acknowledgeNotification,
    fetchNotifications,
} from './apis/notifications';
import { getOrderCancellationReasons } from './apis/orderCancellationReasons';
import {
    fetchOrders,
    checkoutUpdateOrder,
    cancelOrderFromModal,
    fetchOrdersForDataModule,
} from './apis/orders';
import {
    partnerOnboardingSignup,
    verifyPartnerOnboardingCode,
    partnerOnboardingSubmitCardApplication,
    createPassword,
} from './apis/partnerOnboarding';
import {
    paychexOnboardingSignup,
    paychexOnboardingSignupErrorHandler,
} from './apis/paychexOnboarding';
import { createPaymentPlan } from './apis/paymentPlans';
import { fetchPaymentSetupSummary } from './apis/paymentSetupSummary';
import { fetchActivePayments } from './apis/payments';
import { createPaystub, pollPaystub } from './apis/paystubs';
import { fetchPerpayPlusDashboardSummary } from './apis/perpayPlusDashboardSummary';
import {
    perpayPlusSoloApplicationEpic,
    createPerpayPlusSoloFeatureEnrollmentSuccessEpic,
    perpayPlusSoloPayrollComplete,
    perpayPlusSoloPayrollStart,
} from './apis/perpayPlusSolo';
import { fetchPinwheelABTest } from './apis/pinwheelABTest';
import { fetchPinwheelEligibility } from './apis/pinwheelEligibility';
import {
    resendMFACode,
    sendMFACode,
    validateMFACode,
} from './apis/profileMFACode';
import { fetchReferralStats } from './apis/referralStats';
import {
    primaryJob,
    salaryInfo,
    setPhone,
    signup,
    verifyPhone,
} from './apis/signup';
import { createSurveyResponse } from './apis/surveyResponses';
import { fetchPinwheelSurvey } from './apis/surveys';
import { fetchTransactionDetails } from './apis/transactionDetails';
import {
    fetchUserInfo,
    updateUserInfo,
    deactivateAccount,
    cardUpdateUserInfo,
    fetchUserInfoForDataModule,
    updateUserInfoForDataModule,
    updateUserInfoContextSuccess,
    cardApplicationUpdateUserInfo,
    cardApplicationUpdateUserInfoSuccess,
} from './apis/userInfo';
import { updateUserPhone, verifyNewPhone } from './apis/userPhone';
import {
    fetchUserStatus,
    fetchUserStatusForDataModule,
} from './apis/userStatus';
import {
    createWithdrawalRequest,
    fetchWithdrawalBalance,
    fetchWithdrawalBalanceV2,
} from './apis/withdrawal';
import { reportError } from './errors';
import {
    postAuthRedirect,
    routeToJwtLocation,
    routeToLocation,
} from './router';
import {
    updateCardAutoPayStepAfterDateSelection,
    updateCardAutoPayStateAfterFrequencySelection,
    cancelAutoPaySchedule,
    createAutoPayScheduleForCardAutoPay,
} from './ui/autoPaySchedules';
import {
    bankScreenCreateBank,
    createBankAccountForCardAutoPay,
    fetchBankAccountsForCardAutoPay,
    verifySelectBank,
    verifySingleBank,
} from './ui/banks';
import { fetchCardActivation } from './ui/cardActivation';
import { cardConfirmEmployerSetNextStep } from './ui/cardConfirmEmployer';
import {
    cardApplicationPayrollComplete,
    cardApplicationPayrollStart,
    cardPinwheelTransitionToModalMVP2,
    creditUnfreezeCardAccountApplication,
    fetchCardAccountApplicationForOnboarding,
    fetchFeaturesForOnboarding,
    processCardApplication,
    processCardApplicationAddress,
    updateCardAccountApplicationForOnboarding,
} from './ui/cardOnboarding';
import { cardOnboardingMetalReplaceMaterial } from './ui/cardOnboardingMetal';
import {
    updateEmploymentUI,
    updatePaycycleUI,
    cardUpdateSalaryInfo,
    cardCreatePrimaryJob,
} from './ui/employment';
import { profileCreateExternalCard } from './ui/externalCards';
import {
    createFeatureEnrollmentForPerpayPlus,
    fetchFeatureEnrollmentsForPerpayPlus,
} from './ui/featureEnrollments';
import { gateCardAccount } from './ui/gateCardAccount';
import { gateCardApply } from './ui/gateCardApply';
import { fetchPartnerHostedCardApplication } from './ui/partnerOnboardingCardApplication';
import { paymentPortalCreateExternalCard } from './ui/paymentPortal';
import {
    fetchPaymentSetupData,
    paymentSetupCreatePrimaryJob,
    paymentSetupPayrollStart,
    paymentSetupPayrollComplete,
} from './ui/paymentSetup';
import { createPerpayPlusSoloEnrollmentEpic } from './ui/perpayPlusSolo';
import {
    fetchPerpaySplitSetUpPaymentsData,
    perpaySplitPrimaryJob,
    perpaySplitSalaryInfo,
    perpaySplitCreateBank,
} from './ui/perpaySplitSetUpPayments';
import {
    pinwheelCreatePrimaryJob,
    pinwheelUpdateSalaryInfo,
} from './ui/pinwheel';
import {
    pinwheelSplitCreatePrimaryJob,
    pinwheelSplitUpdateSalaryInfo,
} from './ui/pinwheelSplitSetUpPayments';
import {
    loadSupportTool,
    launchSupportTool,
    updateSupportTool,
    shutDownSupportTool,
    loadDecagonSupportTool,
    supportToolIntercomFallback,
} from './ui/supportTool';
import {
    replaceAmountToChargeForUpdateDirectDeposit,
    createPrimaryJobSetStepToUpdate,
    updateJobSetStepToUpdate,
    setStepsForUpdateDirectDeposit,
    acceptCreditLimit,
    updateDirectDepositPaymentSetupComplete,
    updateDirectDepositPaymentSetupStarted,
} from './ui/updateDirectDeposit';
import { updateUserInfoForCardOnboardingMVP2 } from './ui/userInfoForCardOnboardingMVP2';
import {
    addToWallet,
    virtualProvisioningProvisionRequest,
    openAddToWalletLink,
} from './ui/virtualProvisioning';
import { createWithdrawalRequestUI } from './ui/withdrawal';
import { nativeLinkReceived } from './utils/linking';

const staticEpics = [
    acceptCreditLimit,
    acknowledgeNotification,
    analyticsBuffer,
    appUpdate,
    bankScreenCreateBank,
    cancelOrderFromModal,
    cardActivate,
    cardCreatePrimaryJob,
    cardPinwheelTransitionToModalMVP2,
    cardOnboardingMetalReplaceMaterial,
    cardUpdateSalaryInfo,
    cardUpdateUserInfo,
    checkoutUpdateOrder,
    createBank,
    createDeduction,
    createDeposit,
    createEvent,
    createExternalCard,
    createPassword,
    createPaymentPlan,
    createPaystub,
    createPrimaryJobSetStepToUpdate,
    createSurveyResponse,
    createWithdrawalRequest,
    createWithdrawalRequestUI,
    deactivateAccount,
    cardConfirmEmployerSetNextStep,
    disableFeature,
    enrollFeature,
    fetchABTests,
    fetchABTestsV2,
    fetchAccountSummary,
    fetchAds,
    fetchBanks,
    fetchCompanies,
    fetchCompaniesSelfOnboarding,
    fetchCreditLimits,
    fetchCreditScoreHistory,
    fetchCreditScoresSummary,
    fetchDeductions,
    fetchDeductionsForDataModule,
    fetchEnrolledFeatures,
    fetchExternalCards,
    fetchFeatures,
    fetchNotifications,
    fetchOrders,
    fetchOrdersForDataModule,
    fetchPaymentSetupData,
    paymentSetupCreatePrimaryJob,
    fetchPayrollInstructions,
    fetchPerpaySplitSetUpPaymentsData,
    fetchPinwheelEligibility,
    fetchPinwheelSurvey,
    fetchTransactionDetails,
    fetchUserInfo,
    fetchUserStatus,
    fetchWithdrawalBalance,
    fetchWithdrawalBalanceV2,
    getABTest,
    getAccountDeactivationEligibility,
    getAccountDeactivationReasons,
    getJobs,
    getOrderCancellationReasons,
    initializeIdentity,
    authenticationSuccess,
    login,
    logoutAuthService,
    logoutToken,
    paymentPortalCreateExternalCard,
    perpaySplitCreateBank,
    perpaySplitPrimaryJob,
    perpaySplitSalaryInfo,
    pinwheelCreatePrimaryJob,
    pinwheelSplitCreatePrimaryJob,
    pinwheelSplitTransitionToModal,
    pinwheelSplitUpdateSalaryInfo,
    pinwheelTransitionToModal,
    pinwheelUpdateSalaryInfo,
    pollCardAccountApplicationStatus,
    pollPaystub,
    primaryJob,
    processCardApplication,
    processCardApplicationAddress,
    profileCreateExternalCard,
    reEnableFeature,
    refreshToken,
    replaceAmountToChargeForUpdateDirectDeposit,
    reportABTestVariant,
    reportABTestVariantForDataModule,
    reportError,
    resendMFA,
    resetPassword,
    routeToJwtLocation,
    routeToLocation,
    salaryInfo,
    setPhone,
    setStepsForUpdateDirectDeposit,
    signup,
    updateCardAutoPayStepAfterDateSelection,
    updateCardAutoPayStateAfterFrequencySelection,
    updateDeduction,
    updateDeductionAccountId,
    updateDirectDepositPinwheelTransitionToModal,
    updateEmploymentUI,
    updateJobSetStepToUpdate,
    updatePaycycleUI,
    updateUserInfo,
    verifyBank,
    verifyIdentity,
    verifyPhone,
    verifyMFA,
    verifySelectBank,
    verifySingleBank,
    cancelAutoPaySchedule,
    fetchCoreSummary,
    createAutoPaySchedule,
    createAutoPayScheduleForCardAutoPay,
    createBankAccount,
    createBankAccountForCardAutoPay,
    createCardAccountApplications,
    createPrimaryJob,
    creditUnfreezeCardAccountApplication,
    downloadCardStatement,
    fetchAutoPaySchedules,
    fetchBankAccounts,
    fetchBankAccountsForCardAutoPay,
    fetchBorrowerCreditSummary,
    fetchCardAccountApplicationForOnboarding,
    fetchMarketplaceSummaryForOnboarding,
    fetchCardAccountApplication,
    fetchCardAccount,
    freezeCard,
    unfreezeCard,
    fetchCardActivation,
    fetchCardStatements,
    fetchDeductionsForDataModule,
    fetchFeaturesForOnboarding,
    fetchInitiatedAutoPayPayments,
    fetchJobsForDataModule,
    fetchPaymentSetupSummary,
    fetchPayrollInstructionsForDataModule,
    fetchPinwheelABTest,
    fetchUserInfoForDataModule,
    fetchUserStatusForDataModule,
    gateCardAccount,
    gateCardApply,
    fetchGeneralAccountSummary,
    fetchReferralStats,
    updateAutoPaySchedule,
    updateCardAccountApplication,
    updateCardAccountApplicationForOnboarding,
    updateCreditLimit,
    updateJob,
    updateUserInfoForDataModule,
    updateUserInfoForCardOnboardingMVP2,
    uploadCardDocuments,
    fetchBorrowerCredits,
    fetchFeatureEnrollments,
    createFeatureEnrollment,
    createFeatureEnrollmentForPerpayPlus,
    fetchFeatureEnrollmentsForPerpayPlus,
    fetchActivePayments,
    fetchCardActivity,
    analyticsIterablePushNotificationOpen,
    navigateOnNativeUrlPush,
    nativeLinkReceived,
    postAuthRedirect,
    fetchCardDashboardSummary,
    fetchMarketplaceDashboardSummary,
    fetchPlatformDashboardNotifications,
    actionsToNativeFromWeb,
    actionsToWebFromNative,
    fetchPerpayPlusDashboardSummary,
    fetchDirectDepositDashboardSummary,
    fetchLearnMore,
    fetchTrustPilotLink,
    hookActionEpic,
    partnerOnboardingSignup,
    marketplacePartnerOnboardingSignup,
    paychexOnboardingSignup,
    paychexOnboardingSignupErrorHandler,
    verifyPartnerOnboardingCode,
    cleanupMagentoTokenOnLogout,
    cleanupAlgoliaOnLogout,
    partnerOnboardingSubmitCardApplication,
    fetchPartnerHostedCardApplication,
    cardApplicationPayrollStart,
    cardApplicationPayrollComplete,
    fetchFeatureSwitch,
    loadSupportTool,
    launchSupportTool,
    updateSupportTool,
    shutDownSupportTool,
    loadDecagonSupportTool,
    supportToolIntercomFallback,
    cardApplicationUpdateUserInfo,
    cardApplicationUpdateUserInfoSuccess,
    submitCardApplication,
    submitCardApplicationError,
    pollCardApplicationStatus,
    pollCardApplicationStatusRedirect,
    pollCardApplicationStatusTimeoutRedirect,
    updateUserInfoContextSuccess,
    validateAddressForCardMigration,
    handleCardMigrationSuccess,
    fetchAuthTokensSuccess,
    passkeyAuthenticateCompleteSuccess,
    passkeyRegisterLogin,
    passkeyRegisterSignup,
    openBiometricRegistrationFailureModal,
    addToWallet,
    virtualProvisioningProvisionRequest,
    openAddToWalletLink,
    submitDefaultAddress,
    biometricGateEnabledInitialize,
    sendMFACode,
    validateMFACode,
    resendMFACode,
    submitEmail,
    submitMarketplaceCardApplicationSuccess,
    updateUserPhone,
    verifyNewPhone,
    perpayPlusSoloApplicationEpic,
    createPerpayPlusSoloFeatureEnrollmentSuccessEpic,
    perpayPlusSoloPayrollStart,
    perpayPlusSoloPayrollComplete,
    perpayPlusSoloPinwheelStepTransition,
    updateUtmCookie,
    payrollPaymentSetupSuccess,
    updateDirectDepositPaymentSetupComplete,
    updateDirectDepositPaymentSetupStarted,
    paymentSetupPayrollStart,
    paymentSetupPayrollComplete,
    payrollSetupPaychexError,
    createPerpayPlusSoloEnrollmentEpic,

    // --

    cardAccountApplicationCrudDataModule.composedEpic,
    cardOnboardingSwitcherDataModule.composedEpic,
    cardManualPaymentSetupDataModule.composedEpic,
    cardStatementsDataModule.composedEpic,
    directDepositDetailsDataModule.composedEpic,
    ordersDataModule.composedEpic,
    cardPaymentSummaryDataModule.composedEpic,
    userInfoForCardOnboardingMVP2CrudDataModule.composedEpic,
    cardCreateAutoPayDataModule.composedEpic,
    bankAccountsForCardAutoPayDataModule.composedEpic,
    payrollInstructionsFlowDataModule.composedEpic,
    directDepositUpdateEmployerAndAmountDataModule.composedEpic,
    fetchCardDetailsV2DataModule.composedEpic,
    directDepositUpdateManualPaymentSetupDataModule.composedEpic,
    updateDirectDepositDataModule.composedEpic,
    perpayPlusDataModule.composedEpic,
    perpayPlusSoloDataModule.composedEpic,
    perpayPlusSoloApplicationDataModule.composedEpic,
    borrowerCreditsDataModule.composedEpic,
    marketplaceDetailsDataModule.composedEpic,
    featureEnrollmentsForPerpayPlusCrudDataModule.composedEpic,
    cardActivityDataModule.composedEpic,
    checkoutDataModule.composedEpic,
    dashboardV2DataModule.composedEpic,
    marketplaceDetailsNotificationsDataModule.composedEpic,
    cardDetailsNotificationsDataModule.composedEpic,
    pinwheelDataModule.composedEpic,
    partnerOnboardingDataModule.composedEpic,
];

let epicManager = null;

export function getEpicManager() {
    if (!epicManager) {
        epicManager = new EpicManager(staticEpics);
    }

    return epicManager;
}
